import React from 'react';
import Svg, { Path } from 'react-native-svg';

export type ProfileIconId =
  | 'linkedin'
  | 'instagram'
  | 'x'
  | 'youtube'
  | 'github'
  | 'whatsapp'
  | 'tiktok'
  | 'spotify'
  | 'behance'
  | 'dribbble'
  | 'upwork'
  | 'fiverr'
  | 'pinterest'
  | 'snapchat'
  | 'telegram'
  | 'twitch'
  | 'soundcloud'
  | 'facebook'
  | 'discord'
  | 'tabbio'
  | 'email'
  | 'phone'
  | 'landline'
  | 'slack';

export interface BrandChipColor {
  bg: string;
  text: string;
  border?: string;
}

export const PROFILE_BRAND_COLORS: Record<ProfileIconId, BrandChipColor> = {
  linkedin: { bg: '#0A66C2', text: '#FFFFFF' },
  instagram: { bg: '#B900B4', text: '#FFFFFF' },
  x: { bg: '#000000', text: '#FFFFFF' },
  youtube: { bg: '#282828', text: '#FFFFFF' },
  github: { bg: '#000000', text: '#FFFFFF' },
  whatsapp: { bg: '#455A64', text: '#FFFFFF' },
  tiktok: { bg: '#000000', text: '#FFFFFF' },
  spotify: { bg: '#1DB954', text: '#191414' },
  behance: { bg: '#0057FF', text: '#FFFFFF' },
  dribbble: { bg: '#FFFFFF', text: '#000000', border: '#212121' },
  upwork: { bg: '#FFFFFF', text: '#000000', border: '#212121' },
  fiverr: { bg: '#1DBF73', text: '#FFFFFF' },
  pinterest: { bg: '#FFFFFF', text: '#000000', border: '#212121' },
  snapchat: { bg: '#FFFC00', text: '#000000' },
  telegram: { bg: '#3FAEE8', text: '#FFFFFF' },
  twitch: { bg: '#9146FF', text: '#FFFFFF' },
  soundcloud: { bg: '#FF5500', text: '#FFFFFF' },
  facebook: { bg: '#0866FF', text: '#FFFFFF' },
  discord: { bg: '#5865F2', text: '#FFFFFF' },
  tabbio: { bg: '#5A2AFF', text: '#FFFFFF' },
  email: { bg: '#111827', text: '#FFFFFF' },
  phone: { bg: '#374151', text: '#FFFFFF' },
  landline: { bg: '#4B5563', text: '#FFFFFF' },
  slack: { bg: '#4A154B', text: '#FFFFFF' },
};

function IconWrap({
  size,
  children,
}: {
  size: number;
  children: React.ReactNode;
}) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      {children}
    </Svg>
  );
}

function TabbioGlyph({ size = 24, color = 'currentColor' }: { size?: number; color?: string }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 105.81 123.09" fill={color}>
      <Path d="M65.07,99.14v-33.31h29.9v-23.95h-29.9V0H22.24L0,22.24h0v43.59h41.11v29.2c0,15.5,14.88,28.06,33.23,28.06h31.46v-23.95h-40.74ZM8.68,41.87v-16.49h16.35V8.4h16.09v33.48H8.68Z" />
    </Svg>
  );
}

export function ProfileBrandIcon({
  id,
  size = 20,
  color = 'currentColor',
}: {
  id: ProfileIconId;
  size?: number;
  color?: string;
}) {
  if (id === 'tabbio') {
    return <TabbioGlyph size={size} color={color} />;
  }

  if (id === 'email') {
    return (
      <IconWrap size={size}>
        <Path d="M3 6.75C3 5.78 3.78 5 4.75 5h14.5C20.22 5 21 5.78 21 6.75v10.5c0 .97-.78 1.75-1.75 1.75H4.75A1.75 1.75 0 0 1 3 17.25V6.75Z" stroke={color} strokeWidth={2} />
        <Path d="m4 7 8 6 8-6" stroke={color} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
      </IconWrap>
    );
  }

  if (id === 'phone' || id === 'landline') {
    return (
      <IconWrap size={size}>
        <Path d="M20.5 16.2V19a2 2 0 0 1-2.18 2A17.8 17.8 0 0 1 10.6 18a17.3 17.3 0 0 1-5.6-5.6A17.8 17.8 0 0 1 2 4.7 2 2 0 0 1 4 2.5h2.8a2 2 0 0 1 1.9 1.6c.13.9.34 1.77.66 2.59a2 2 0 0 1-.45 2.1l-1.2 1.2a14 14 0 0 0 6.3 6.3l1.2-1.2a2 2 0 0 1 2.1-.45c.82.32 1.69.53 2.59.66a2 2 0 0 1 1.6 1.9Z" stroke={color} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
      </IconWrap>
    );
  }

  if (id === 'slack') {
    return (
      <Svg width={size} height={size} viewBox="0 0 24 24" fill={color}>
        <Path d="M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.527 2.527 0 0 1 2.522-2.52h2.52v2.52zM6.313 15.165a2.527 2.527 0 0 1 2.521-2.52 2.527 2.527 0 0 1 2.521 2.52v6.313A2.528 2.528 0 0 1 8.834 24a2.528 2.528 0 0 1-2.521-2.522v-6.313zM8.834 5.042a2.528 2.528 0 0 1-2.521-2.52A2.528 2.528 0 0 1 8.834 0a2.528 2.528 0 0 1 2.521 2.522v2.52H8.834zM8.834 6.313a2.528 2.528 0 0 1 2.521 2.521 2.528 2.528 0 0 1-2.521 2.521H2.522A2.528 2.528 0 0 1 0 8.834a2.528 2.528 0 0 1 2.522-2.521h6.312zM18.956 8.834a2.528 2.528 0 0 1 2.522-2.521A2.528 2.528 0 0 1 24 8.834a2.528 2.528 0 0 1-2.522 2.521h-2.522V8.834zM17.688 8.834a2.528 2.528 0 0 1-2.523 2.521 2.527 2.527 0 0 1-2.52-2.521V2.522A2.527 2.527 0 0 1 15.165 0a2.528 2.528 0 0 1 2.523 2.522v6.312zM15.165 18.956a2.528 2.528 0 0 1 2.523 2.522A2.528 2.528 0 0 1 15.165 24a2.527 2.527 0 0 1-2.52-2.522v-2.522h2.52zM15.165 17.688a2.527 2.527 0 0 1-2.52-2.523 2.526 2.526 0 0 1 2.52-2.52h6.313A2.527 2.527 0 0 1 24 15.165a2.528 2.528 0 0 1-2.522 2.523h-6.313z" />
      </Svg>
    );
  }

  if (id === 'linkedin') {
    return (
      <Svg width={size} height={size} viewBox="0 0 24 24" fill={color}>
        <Path d="M20.5 2h-17A1.5 1.5 0 002 3.5v17A1.5 1.5 0 003.5 22h17a1.5 1.5 0 001.5-1.5v-17A1.5 1.5 0 0020.5 2zM8 19H5v-9h3zM6.5 8.25A1.75 1.75 0 118.3 6.5a1.78 1.78 0 01-1.8 1.75zM19 19h-3v-4.74c0-1.42-.6-1.93-1.38-1.93A1.74 1.74 0 0013 14.19a.66.66 0 000 .14V19h-3v-9h2.9v1.3a3.11 3.11 0 012.7-1.4c1.55 0 3.36.86 3.36 3.66z" />
      </Svg>
    );
  }

  if (id === 'instagram') {
    return (
      <Svg width={size} height={size} viewBox="0 0 24 24" fill={color}>
        <Path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
      </Svg>
    );
  }

  if (id === 'x') {
    return (
      <Svg width={size} height={size} viewBox="0 0 24 24" fill={color}>
        <Path d="M18.901 1.153h3.68l-8.04 9.19L24 22.846h-7.406l-5.8-7.584-6.638 7.584H.474l8.6-9.83L0 1.154h7.594l5.243 6.932ZM17.61 20.644h2.039L6.486 3.24H4.298Z" />
      </Svg>
    );
  }

  if (id === 'youtube') {
    return (
      <Svg width={size} height={size} viewBox="0 0 24 24" fill={color}>
        <Path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
      </Svg>
    );
  }

  if (id === 'github') {
    return (
      <Svg width={size} height={size} viewBox="0 0 24 24" fill={color}>
        <Path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" />
      </Svg>
    );
  }

  if (id === 'whatsapp') {
    return (
      <Svg width={size} height={size} viewBox="0 0 24 24" fill={color}>
        <Path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.484 3.488" />
      </Svg>
    );
  }

  if (id === 'tiktok') {
    return (
      <Svg width={size} height={size} viewBox="0 0 24 24" fill={color}>
        <Path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z" />
      </Svg>
    );
  }

  if (id === 'spotify') {
    return (
      <Svg width={size} height={size} viewBox="0 0 24 24" fill={color}>
        <Path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.42 1.56-.299.421-1.02.599-1.559.3z" />
      </Svg>
    );
  }

  if (id === 'pinterest') {
    return (
      <Svg width={size} height={size} viewBox="0 0 24 24" fill={color}>
        <Path d="M12.017 0C5.396 0 .029 5.367.029 11.987c0 5.079 3.158 9.417 7.618 11.162-.105-.949-.199-2.403.041-3.439.219-.937 1.406-5.957 1.406-5.957s-.359-.72-.359-1.781c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738a.36.36 0 0 1 .083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.631-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12.017 24c6.624 0 11.99-5.367 11.99-11.988C24.007 5.367 18.641 0 12.017 0z" />
      </Svg>
    );
  }

  if (id === 'snapchat') {
    return (
      <Svg width={size} height={size} viewBox="0 0 24 24" fill={color}>
        <Path d="M12.206.793c.99 0 4.347.276 5.93 3.821.529 1.193.403 3.219.299 4.847l-.003.06c-.012.18-.022.345-.03.51.075.045.203.09.401.09.3-.016.659-.12 1.033-.301.165-.088.344-.104.464-.104.182 0 .359.029.509.09.45.149.734.479.734.838.015.449-.39.839-1.213 1.168-.089.029-.209.075-.344.119-.45.135-1.139.36-1.333.81-.09.224-.061.524.12.868l.015.015c.06.136 1.526 3.475 4.791 4.014.255.044.435.27.42.509 0 .075-.015.149-.045.225-.24.569-1.273.988-3.146 1.271-.059.091-.12.375-.164.57-.029.179-.074.36-.134.553-.076.271-.27.405-.555.405h-.03a3.174 3.174 0 0 1-.659-.091c-.375-.09-.8-.18-1.29-.18-.24 0-.48.029-.72.09-.57.135-1.02.631-1.56 1.246-.765.869-1.65 1.86-3.271 1.86h-.06c-1.62 0-2.505-.991-3.271-1.86-.54-.615-.99-1.111-1.56-1.246a3.3 3.3 0 0 0-.72-.09c-.49 0-.915.091-1.29.18-.24.06-.435.091-.659.091-.3 0-.48-.135-.555-.405a4.26 4.26 0 0 1-.134-.553c-.044-.195-.105-.479-.164-.57-1.873-.283-2.906-.702-3.146-1.271a.529.529 0 0 1-.045-.225c-.015-.24.165-.465.42-.509 3.264-.54 4.73-3.879 4.791-4.02l.016-.029c.18-.345.21-.645.119-.869-.195-.434-.884-.659-1.332-.809a3.72 3.72 0 0 1-.345-.12c-.6-.255-1.065-.584-1.168-.899-.03-.09-.045-.18-.045-.271 0-.404.391-.689.735-.838.149-.06.344-.09.524-.09.165 0 .314.015.449.09.374.195.74.301 1.032.301.196 0 .329-.045.404-.091-.01-.165-.021-.33-.033-.51l-.004-.06c-.104-1.627-.229-3.654.3-4.847C7.86 1.069 11.216.793 12.206.793z" />
      </Svg>
    );
  }

  if (id === 'telegram') {
    return (
      <Svg width={size} height={size} viewBox="0 0 24 24" fill={color}>
        <Path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" />
      </Svg>
    );
  }

  if (id === 'twitch') {
    return (
      <Svg width={size} height={size} viewBox="0 0 24 24" fill={color}>
        <Path d="M11.571 4.714h1.715v5.143H11.57zm4.715 0H18v5.143h-1.714zM6 0L1.714 4.286v15.428h5.143V24l4.286-4.286h3.428L22.286 12V0zm14.571 11.143l-3.428 3.428h-3.429l-3 3v-3H6.857V1.714h13.714z" />
      </Svg>
    );
  }

  if (id === 'facebook') {
    return (
      <Svg width={size} height={size} viewBox="0 0 24 24" fill={color}>
        <Path d="M24 12.073c0-6.627-5.373-12-12-12S0 5.446 0 12.073c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
      </Svg>
    );
  }

  if (id === 'soundcloud') {
    return (
      <Svg width={size} height={size} viewBox="0 0 24 24" fill={color}>
        <Path d="M6.02 18.58h.84V8.53h-.84v10.05Zm-1.67 0h.83V9.84h-.83v8.74ZM2.69 18.58h.83V11.1h-.83v7.48Zm4.99 0h.83V8.12h-.83v10.46Zm1.67 0h.84V8.53h-.84v10.05Zm1.67 0h.84V9.36h-.84v9.22Zm1.67 0h.83V10.2h-.83v8.38Zm1.67 0h.84v-7.5h-.84v7.5Zm1.67-7.08a3.28 3.28 0 0 0-3.19 2.48v4.6h6.86a2.64 2.64 0 0 0 .18-5.28 3.32 3.32 0 0 0-3.85-1.8Z" />
      </Svg>
    );
  }

  if (id === 'discord') {
    return (
      <IconWrap size={size}>
        <Path d="M20.317 4.369A19.791 19.791 0 0 0 15.885 3a13.66 13.66 0 0 0-.617 1.27 18.27 18.27 0 0 0-5.536 0A13.68 13.68 0 0 0 9.115 3a19.736 19.736 0 0 0-4.435 1.372C1.883 8.537 1.132 12.598 1.5 16.603a19.94 19.94 0 0 0 5.44 2.76 14.38 14.38 0 0 0 1.17-1.93 12.97 12.97 0 0 1-1.84-.9c.16-.12.32-.25.47-.38 3.55 1.67 7.39 1.67 10.9 0 .15.13.31.26.47.38-.59.36-1.2.66-1.84.9.34.67.73 1.31 1.17 1.93a19.9 19.9 0 0 0 5.44-2.76c.45-4.68-.75-8.7-3.18-12.23ZM8.68 14.62c-1.06 0-1.93-.97-1.93-2.16 0-1.2.86-2.17 1.93-2.17s1.94.97 1.93 2.17c0 1.19-.86 2.16-1.93 2.16Zm6.64 0c-1.06 0-1.93-.97-1.93-2.16 0-1.2.86-2.17 1.93-2.17s1.94.97 1.93 2.17c0 1.19-.86 2.16-1.93 2.16Z" fill={color} />
      </IconWrap>
    );
  }

  if (id === 'dribbble') {
    return (
      <Svg width={size} height={size} viewBox="0 0 24 24" fill={color}>
        <Path d="M12 24C5.385 24 0 18.615 0 12S5.385 0 12 0s12 5.385 12 12-5.385 12-12 12zm10.12-10.358c-.35-.11-3.17-.953-6.384-.438 1.34 3.684 1.887 6.684 1.992 7.308 2.3-1.555 3.936-4.02 4.395-6.87zm-6.115 7.808c-.153-.9-.75-4.032-2.19-7.77l-.066.02c-5.79 2.015-7.86 6.025-8.04 6.4 1.73 1.358 3.92 2.166 6.29 2.166 1.42 0 2.77-.29 4-.81zm-11.62-2.58c.232-.4 3.045-5.055 8.332-6.765.135-.045.27-.084.405-.12-.26-.585-.54-1.167-.832-1.74C7.17 11.775 2.206 11.71 1.756 11.7l-.004.312c0 2.633.998 5.037 2.634 6.855zm-2.42-8.955c.46.008 4.683.026 9.477-1.248-1.698-3.018-3.53-5.558-3.8-5.928-2.868 1.35-5.01 3.99-5.676 7.17zM9.6 2.052c.282.38 2.145 2.914 3.822 6 3.645-1.365 5.19-3.44 5.373-3.702C16.86 2.608 14.545 1.635 12 1.635c-.82 0-1.62.1-2.4.285v.132zm10.335 3.483c-.218.29-1.91 2.493-5.724 4.04.24.49.47.985.68 1.486.08.18.15.36.22.53 3.41-.43 6.8.26 7.14.33-.02-2.42-.88-4.64-2.31-6.38z" />
      </Svg>
    );
  }

  if (id === 'behance') {
    return (
      <Svg width={size} height={size} viewBox="0 0 24 24" fill={color}>
        <Path d="M6.938 4.503c.702 0 1.34.06 1.92.188.577.13 1.07.33 1.485.61.41.28.733.65.96 1.12.225.47.34 1.05.34 1.73 0 .74-.17 1.36-.507 1.86-.338.5-.837.9-1.502 1.22.906.26 1.576.72 2.022 1.37.448.66.665 1.45.665 2.36 0 .75-.13 1.39-.41 1.93-.28.55-.67 1-1.16 1.35-.48.348-1.05.6-1.67.767-.66.165-1.32.248-2.004.248H0V4.51h6.938v-.007zM6.545 10.16c.6 0 1.1-.15 1.47-.45.38-.3.56-.73.56-1.29 0-.31-.05-.57-.17-.78-.11-.21-.27-.38-.47-.51-.2-.13-.43-.22-.69-.27-.27-.05-.56-.08-.87-.08H3.41v3.38h3.135zm.17 5.69c.35 0 .68-.04.98-.12.31-.08.57-.2.79-.37.22-.16.39-.38.52-.64.12-.27.18-.6.18-.99 0-.79-.22-1.36-.66-1.7-.44-.34-1.02-.51-1.73-.51H3.41v4.33h3.305zM15.4 4.503v2.14h5.33V4.503H15.4zm6.6 6.15c0-.78-.14-1.502-.41-2.164a5.04 5.04 0 00-1.16-1.72 5.36 5.36 0 00-1.8-1.14c-.7-.28-1.47-.42-2.32-.42-.84 0-1.61.14-2.31.42a5.52 5.52 0 00-1.82 1.14c-.51.49-.91 1.07-1.19 1.72-.29.66-.43 1.384-.43 2.164 0 .8.14 1.53.43 2.19a5 5 0 001.19 1.72c.51.48 1.12.86 1.82 1.12.7.26 1.47.39 2.31.39.93 0 1.73-.18 2.39-.54.66-.36 1.21-.78 1.63-1.27l-1.95-1.7c-.24.36-.55.63-.91.82-.37.19-.8.29-1.28.29-.58 0-1.09-.14-1.53-.42-.44-.28-.72-.74-.85-1.38h6.92c.02-.17.03-.33.03-.48zM19.08 11.3h-4.6c.07-.57.28-1.03.63-1.38.36-.35.83-.52 1.42-.52.62 0 1.1.17 1.43.51.34.34.56.8.68 1.39h.44z" />
      </Svg>
    );
  }

  if (id === 'upwork') {
    return (
      <Svg width={size} height={size} viewBox="0 0 24 24" fill={color}>
        <Path d="M18.561 13.158c-1.102 0-2.135-.467-3.074-1.227l.228-1.076.008-.042c.207-1.143.849-3.06 2.839-3.06 1.492 0 2.703 1.212 2.703 2.703-.001 1.489-1.212 2.702-2.704 2.702zm0-8.14c-2.539 0-4.51 1.649-5.31 4.366-1.22-1.834-2.148-4.036-2.687-5.892H7.828v7.112c-.002 1.406-1.141 2.546-2.547 2.548-1.405-.002-2.543-1.143-2.545-2.548V3.492H0v7.112c0 2.914 2.37 5.303 5.281 5.303 2.913 0 5.283-2.389 5.283-5.303v-1.19c.529 1.107 1.182 2.229 1.974 3.221l-1.673 7.873h2.797l1.213-5.71c1.063.679 2.285 1.109 3.686 1.109 3 0 5.439-2.452 5.439-5.45 0-3-2.439-5.439-5.439-5.439z" />
      </Svg>
    );
  }

  if (id === 'fiverr') {
    return (
      <Svg width={size} height={size} viewBox="0 0 24 24" fill={color}>
        <Path d="M23.004 15.588a.995.995 0 1 0 .002-1.99.995.995 0 0 0-.002 1.99zm-.996-3.705h-.85c-.546 0-.84.41-.84 1.092v2.466h-1.61v-3.558h-.684c-.547 0-.84.41-.84 1.092v2.466h-1.61V11.14h1.468l.142.858h.062c.268-.63.793-.982 1.472-.982h.94c.268-.63.793-.982 1.472-.982h1.078v4.85h-1.2v-3.001zm-7.836-1.849h1.61V14.883h-1.61V10.034zm0-2.483h1.61v1.669h-1.61V7.55zm-3.484 7.457c-1.656 0-2.67-1.218-2.67-2.975 0-1.77 1.014-2.975 2.67-2.975.578 0 1.032.202 1.382.483l-.532 1.2a.96.96 0 0 0-.636-.255c-.602 0-.96.56-.96 1.547 0 .973.358 1.547.96 1.547a.96.96 0 0 0 .636-.255l.532 1.2c-.35.281-.804.483-1.382.483zm-4.484 0c-1.656 0-2.67-1.218-2.67-2.975 0-1.77 1.014-2.975 2.67-2.975.578 0 1.032.202 1.382.483l-.532 1.2a.96.96 0 0 0-.636-.255c-.602 0-.96.56-.96 1.547 0 .973.358 1.547.96 1.547a.96.96 0 0 0 .636-.255l.532 1.2c-.35.281-.804.483-1.382.483zM.872 12.976H0V11.14h.872V9.482h1.61v1.658h1.108v1.836H2.482v1.54c0 .396.174.54.498.54h.61v1.736H2.39c-1.014 0-1.518-.63-1.518-1.638v-2.178z" />
      </Svg>
    );
  }

  return <ProfileBrandIcon id="linkedin" size={size} color={color} />;
}

export function getProfileBrand(id: ProfileIconId) {
  return PROFILE_BRAND_COLORS[id] ?? { bg: '#E5E7EB', text: '#111827', border: '#D1D5DB' };
}
